import 'core-js/modules/es.error.to-string.js';
import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.date.to-string.js';
import 'core-js/modules/es.number.is-nan.js';
import 'core-js/modules/es.number.max-safe-integer.js';
import 'core-js/modules/es.number.min-safe-integer.js';
import 'core-js/modules/es.number.to-fixed.js';
import 'core-js/modules/es.object.assign.js';
import 'core-js/modules/es.object.to-string.js';
import 'core-js/modules/es.parse-float.js';
import 'core-js/modules/es.regexp.to-string.js';
import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import React, { useRef } from 'react';
import { Input as Input$1 } from '@mui/base/Input';
import classNames from 'classnames';
import { getStatusIcon, ClearAllIcon, CaretUpIcon, CaretDownIcon } from '@cognite/cogs-icons';
import { getThemeClassNames, IconWrapper } from '@cognite/cogs-utils';
import { Body } from '../../tokens/typography/typography.js';
import { TextLabel } from '../_technical/text-label/text-label.js';
import { Button } from '../button/button.js';
import { Shortcut } from '../shortcut/shortcut.js';

// accepts a number and limits the result to a fixed precision.
// returns string representation of the number, limited to `PRECISION` decimal places.
var PRECISION = 9;
var decimalSafe = function decimalSafe(n) {
  var fixed = parseFloat(n.toFixed(PRECISION));
  return fixed.toString();
};
/**
 * Input
 * =============================================================================
 * Describe what your component does, and why it could be useful for users.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-input--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Input>
 *   My input
 * </Input>
 *
 * <Input subject="bird">
 *   My advanced input
 * </Input>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param subject - Explain here what your prop does
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-input--base)
 */
var Input = /*#__PURE__*/React.forwardRef(function (_a, outerRef) {
  var _b, _c, _d;
  var buttonProps = _a.buttonProps,
    className = _a.className,
    clearable = _a.clearable,
    disabled = _a.disabled,
    icon = _a.icon,
    id = _a.id,
    fullWidth = _a.fullWidth,
    helpText = _a.helpText,
    inverted = _a.inverted,
    label = _a.label,
    onChange = _a.onChange,
    onKeyDown = _a.onKeyDown,
    onKeyUp = _a.onKeyUp,
    prefix = _a.prefix,
    _a$size = _a.size,
    size = _a$size === undefined ? 'medium' : _a$size,
    status = _a.status,
    statusText = _a.statusText,
    style = _a.style,
    suffix = _a.suffix,
    _a$step = _a.step,
    step = _a$step === undefined ? 1 : _a$step,
    type = _a.type,
    value = _a.value,
    _a$textAlign = _a.textAlign,
    textAlign = _a$textAlign === undefined ? 'left' : _a$textAlign,
    _a$variant = _a.variant,
    variant = _a$variant === undefined ? 'outline' : _a$variant,
    shortcutKeys = _a.shortcutKeys,
    themeProps = _a.themeProps,
    max = _a.max,
    min = _a.min,
    hideSpinButtons = _a.hideSpinButtons,
    inputStyle = _a.inputStyle,
    otherProps = __rest(_a, ["buttonProps", "className", "clearable", "disabled", "icon", "id", "fullWidth", "helpText", "inverted", "label", "onChange", "onKeyDown", "onKeyUp", "prefix", "size", "status", "statusText", "style", "suffix", "step", "type", "value", "textAlign", "variant", "shortcutKeys", "themeProps", "max", "min", "hideSpinButtons", "inputStyle"]);
  var isNumberInput = type === 'number';
  var isNumberInputWithSpinButtons = isNumberInput && !hideSpinButtons;
  var shouldDisplayLabel = !!label && variant !== 'ghost';
  var shouldHaveRightPadding = !buttonProps && (!clearable || suffix || shortcutKeys) && !isNumberInputWithSpinButtons;
  var innerRef = useRef(null);
  var ref = outerRef || innerRef;
  var maxNumber = parseFloat((_b = max === null || max === undefined ? undefined : max.toString()) !== null && _b !== undefined ? _b : Number.MAX_SAFE_INTEGER.toString());
  var minNumber = parseFloat((_c = min === null || min === undefined ? undefined : min.toString()) !== null && _c !== undefined ? _c : Number.MIN_SAFE_INTEGER.toString());
  var valueAsFloat = parseFloat((_d = value === null || value === undefined ? undefined : value.toString()) !== null && _d !== undefined ? _d : '');
  var shouldDisableIncrement = Number.isNaN(value) || valueAsFloat >= maxNumber;
  var shouldDisableDecrement = Number.isNaN(value) || valueAsFloat <= minNumber;
  var StatusIcon = getStatusIcon(status);
  var handleClear = function handleClear(e) {
    if (onChange && (ref === null || ref === undefined ? undefined : ref.current)) {
      var currentTarget = ref.current.cloneNode(true);
      currentTarget.value = '';
      var event = Object.assign(Object.assign({}, e), {
        target: currentTarget,
        currentTarget: currentTarget
      });
      onChangeLocal(event);
    }
  };
  var handleIncrement = function handleIncrement(e) {
    if (onChange && (ref === null || ref === undefined ? undefined : ref.current)) {
      var currentTarget = ref.current.cloneNode(true);
      var parsedFloat = parseFloat(currentTarget.value);
      if (!Number.isNaN(parsedFloat)) {
        var sum = parsedFloat + step;
        var incremented = decimalSafe(Math.min(sum, maxNumber));
        currentTarget.value = incremented;
      } else {
        currentTarget.value = step.toString();
      }
      var event = Object.assign(Object.assign({}, e), {
        target: currentTarget,
        currentTarget: currentTarget
      });
      onChange(event);
    }
  };
  var handleDecrement = function handleDecrement(e) {
    if (onChange && (ref === null || ref === undefined ? undefined : ref.current)) {
      var currentTarget = ref.current.cloneNode(true);
      var parsedFloat = parseFloat(currentTarget.value);
      if (!Number.isNaN(parsedFloat)) {
        var difference = parsedFloat - step;
        var decremented = decimalSafe(Math.max(difference, minNumber));
        currentTarget.value = decremented;
      } else {
        currentTarget.value = (-1 * step).toString();
      }
      var event = Object.assign(Object.assign({}, e), {
        target: currentTarget,
        currentTarget: currentTarget
      });
      onChange(event);
    }
  };
  var onChangeLocal = function onChangeLocal(event) {
    var _a;
    if (isNumberInput) {
      var currentTarget = event.currentTarget;
      var currentValue = parseFloat(currentTarget.value);
      if (currentValue > maxNumber || currentValue < minNumber) {
        currentTarget.value = (_a = value === null || value === undefined ? undefined : value.toString()) !== null && _a !== undefined ? _a : '';
      }
      onChange === null || onChange === undefined ? undefined : onChange(Object.assign(Object.assign({}, event), {
        target: currentTarget,
        currentTarget: currentTarget
      }));
    } else {
      onChange === null || onChange === undefined ? undefined : onChange(event);
    }
  };
  return jsxs("div", {
    "data-testid": "input-container",
    className: classNames.apply(undefined, ['cogs-v10', 'cogs-input-container', fullWidth && 'cogs-input-container-full-width'].concat(getThemeClassNames(inverted, themeProps))),
    children: [shouldDisplayLabel && (typeof label === 'string' ? jsx(TextLabel, {
      htmlFor: id,
      inverted: inverted,
      text: label
    }) : jsx(TextLabel, Object.assign({
      htmlFor: id,
      inverted: inverted
    }, label))), jsx(Input$1, {
      className: classNames('cogs-v10', 'cogs-input', "cogs-input--size-" + size, "cogs-input--variant-" + variant, "cogs-input--status-" + status, textAlign && "cogs-input-text-align-" + textAlign, disabled && "cogs-input-disabled", fullWidth && 'cogs-input-full-width', !shouldHaveRightPadding && "cogs-input-with-no-right-padding", className),
      style: style,
      disabled: disabled,
      endAdornment: buttonProps || clearable && !!value || suffix || shortcutKeys || isNumberInputWithSpinButtons ? jsxs("div", {
        className: classNames('cogs-v10', 'cogs-input-adornment', "cogs-input-adornment--size-" + size, 'cogs-input-adornment-end', "cogs-input-adornment-end--size-" + size),
        children: [clearable && !!value && jsx(Button, {
          icon: jsx(ClearAllIcon, {}),
          "aria-label": "ClearAll",
          inverted: inverted,
          onClick: handleClear,
          disabled: disabled,
          size: size,
          type: "ghost"
        }), suffix && jsx(Body, {
          size: "xx-small",
          className: "cogs-v10 cogs-input__suffix",
          children: suffix
        }), shortcutKeys && jsx(Shortcut, {
          keys: shortcutKeys,
          inverted: inverted
        }), buttonProps && jsx(Button, Object.assign({}, buttonProps, {
          inverted: inverted,
          size: size,
          type: "ghost"
        })), isNumberInputWithSpinButtons && jsxs("div", {
          className: "cogs-v10 cogs-input-number-arrows-container",
          children: [jsx("button", {
            className: "cogs-v10 cogs-input-number-arrow cogs-input-number-arrow-up",
            "data-testid": "input-increment-button",
            onClick: handleIncrement,
            type: "button",
            disabled: disabled || shouldDisableIncrement,
            children: jsx(IconWrapper, {
              size: size === 'small' ? 12 : 16,
              children: jsx(CaretUpIcon, {
                className: "cogs-v10 cogs-input-number-arrow-icon cogs-input-number-arrow-icon-up"
              })
            })
          }), jsx("div", {
            className: "cogs-v10 cogs-input-number-arrows-divider",
            children: "\xA0"
          }), jsx("button", {
            className: "cogs-v10 cogs-input-number-arrow cogs-input-number-arrow-down",
            "data-testid": "input-decrement-button",
            onClick: handleDecrement,
            type: "button",
            disabled: disabled || shouldDisableDecrement,
            children: jsx(IconWrapper, {
              size: size === 'small' ? 12 : 16,
              children: jsx(CaretDownIcon, {
                className: "cogs-v10 cogs-input-number-arrow-icon cogs-input-number-arrow-icon-down"
              })
            })
          })]
        })]
      }) : undefined,
      id: id,
      onChange: onChangeLocal,
      onKeyDown: onKeyDown,
      onKeyUp: onKeyUp,
      startAdornment: icon || prefix ? jsxs("div", {
        className: classNames('cogs-v10', 'cogs-input-adornment', "cogs-input-adornment--size-" + size, 'cogs-input-adornment-start', "cogs-input-adornment-start--size-" + size),
        children: [icon && jsx("div", {
          className: classNames('cogs-v10', 'cogs-input-adornment-start-icon'),
          children: jsx(IconWrapper, {
            size: 16,
            children: icon
          })
        }), prefix && jsx(Body, {
          size: "xx-small",
          children: prefix
        })]
      }) : undefined,
      value: value,
      slotProps: {
        input: Object.assign({
          ref: ref,
          step: step,
          min: min,
          max: max,
          style: Object.assign(Object.assign({}, inputStyle), {
            textAlign: textAlign
          })
        }, otherProps)
      },
      type: type
    }), helpText && jsx(Body, {
      size: "x-small",
      className: "cogs-v10 cogs-input-help-text",
      children: helpText
    }), status && statusText && jsxs("div", {
      className: "cogs-v10 cogs-input-status-text-container",
      children: [StatusIcon && jsx(IconWrapper, {
        size: 12,
        children: jsx(StatusIcon, {
          className: classNames('cogs-v10', 'cogs-input-status-text', "cogs-input-status-text--status-" + status)
        })
      }), jsx(Body, {
        size: "x-small",
        className: classNames('cogs-v10', 'cogs-input-status-text', "cogs-input-status-text--status-" + status),
        children: statusText
      })]
    })]
  });
});

export { Input };
